import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import ChurchHero from "@/components/ChurchHero";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { api } from "@/lib/api";
import { filterEnabled, isEnabled, applyTileOrder } from "@/lib/features";
import GalleryStrip from "@/components/GalleryStrip";
import LiveBadge from "@/components/LiveBadge";
import { useLiveStatus } from "@/lib/useLiveStatus";
import {
  Megaphone, Users, Crown, BookHeart, HeartHandshake, Radio,
  HandHeart, GraduationCap, HandCoins, Car, ChevronRight,
  CalendarDays, BookText, Landmark, MessageCircle, Images, CalendarCheck, Cake,
} from "lucide-react";

const FEATURES = [
  { to: "/announcements", icon: Megaphone, key: "announcements" },
  { to: "/events", icon: CalendarDays, key: "events" },
  { to: "/yearbook", icon: Users, key: "yearbook" },
  { to: "/chat", icon: MessageCircle, key: "messages" },
  { to: "/board", icon: Crown, key: "board" },
  { to: "/pastor", icon: BookHeart, key: "pastor" },
  { to: "/first-elder", icon: BookHeart, key: "firstElder" },
  { to: "/departments", icon: HeartHandshake, key: "departments" },
  { to: "/live", icon: Radio, key: "live" },
  { to: "/prayer", icon: HandHeart, key: "prayers" },
  { to: "/public/sabbath-lessons", icon: GraduationCap, key: "sabbathLessons" },
  { to: "/tithes", icon: HandCoins, key: "giving" },
  { to: "/sabbath-set", icon: CalendarCheck, key: "sabbathSet" },
];

export default function Dashboard() {
  const { user, church } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [todaySet, setTodaySet] = useState(null);
  const [birthdays, setBirthdays] = useState([]);
  const [wishBusy, setWishBusy] = useState(false);
  const liveStatus = useLiveStatus(church?.church_code);

  // Show the full profile name (e.g. "Welcome, Marie Jean") rather than
  // only the first name. Falls back to the plain "Welcome" if the profile
  // has no name set yet.
  const displayName = user?.name?.trim() || "";
  const sabbathEnabled = isEnabled(
    church?.feature_flags, "sabbathSet", "member", church?._platform_disabled,
  );
  const birthdaysEnabled = isEnabled(
    church?.feature_flags, "birthdays", "member", church?._platform_disabled,
  );

  useEffect(() => {
    if (!sabbathEnabled) return;
    const today = new Date().toISOString().slice(0, 10);
    api.get("/sabbath-sets/current").then((r) => {
      const s = r.data;
      // Only show banner when the current set is TODAY's Sabbath (not a
      // future one) — the tile handles "upcoming".
      if (s && s.sabbath_date === today) setTodaySet(s);
    }).catch(() => {});
  }, [sabbathEnabled]);

  // Birthday banner — fetch every dashboard load. `birthdays` includes
  // the current user if today's their day; the render decides between
  // the self-flavored message vs the "wish X" prompt.
  useEffect(() => {
    if (!birthdaysEnabled) return;
    api.get("/members/birthdays/today")
      .then((r) => setBirthdays(r.data || []))
      .catch(() => setBirthdays([]));
  }, [birthdaysEnabled]);

  const selfBirthday = birthdays.find((b) => b.id === user?.id);
  const otherBirthdays = birthdays.filter((b) => b.id !== user?.id);

  /**
   * Open a direct chat with the birthday person, seeded with a warm
   * "Happy birthday" greeting. The template picks up the recipient's
   * first name so it feels personal. The recipient's chat page reads
   * `?prefill=` on mount and drops the template into the composer —
   * the sender still taps Send explicitly (so they can tweak it).
   */
  const wishBirthday = async (target) => {
    if (!target || wishBusy) return;
    const firstName = target.first_name?.trim()
      || target.name?.split(" ")[0]?.trim()
      || "friend";
    const template = t("birthdayWishTemplate").replace("{name}", firstName);
    setWishBusy(true);
    try {
      const { data } = await api.post(`/conversations/direct/${target.id}`);
      navigate(`/chat/${data.id}?prefill=${encodeURIComponent(template)}`);
    } catch (e) {
      // If we couldn't open the direct chat (e.g. dm disabled between
      // these two users), fall back to the member profile so the user
      // can still find another path to celebrate.
      navigate(`/members/${target.id}`);
    } finally {
      setWishBusy(false);
    }
  };

  return (
    <Layout>
      {/* Full-bleed shared hero (matches public Landing). We only pass
          the church + a personalized eyebrow — title / subtitle / hide
          flags are resolved inside ChurchHero so admin edits (or the
          "Hide" toggles) take effect everywhere at once. */}
      <ChurchHero
        church={church}
        eyebrow={displayName ? `${t("welcome")}, ${displayName}` : undefined}
        testId="dashboard-hero"
        isLive={liveStatus.is_live}
        onLiveTap={() => navigate("/live")}
      />

      <div className="px-5 mt-6 space-y-5 pb-4">
        {/* Birthday banner — appears on the birthday person's own day
            AND if any other church member has a birthday today, invites
            everyone to send warm wishes via chat.
            Now moved to a compact pill next to the "Explore quick
            actions" heading — see below. This entire block is retained
            only for its comment (kept for git-blame archaeology). */}

        {/* Sabbath-day banner — only appears when today is Saturday AND
            a published set list exists for today. Members can dismiss
            the tile grid noise by tapping straight into the set. */}
        {todaySet && (
          <Link
            to={`/sabbath-set/${todaySet.id}`}
            data-testid="sabbath-set-banner"
            className="flex items-center gap-3 rounded-2xl p-4 text-white hover:opacity-95 transition"
            style={{ backgroundColor: "var(--tenant-primary)" }}
          >
            <div className="w-11 h-11 rounded-xl bg-white/15 grid place-items-center shrink-0">
              <CalendarCheck className="w-5 h-5" strokeWidth={1.7} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="label-eyebrow text-white/80">{t("sabbathSetToday")}</div>
              <div className="font-serif-display font-semibold text-sm truncate">
                {todaySet.title || t("sabbathSetHeading")}
              </div>
              <div className="text-[11px] opacity-80 line-clamp-1">
                {(todaySet.items || []).length} items · {t("sabbathSetShareCTA")}
              </div>
            </div>
            <ChevronRight className="w-4 h-4 opacity-80 shrink-0" />
          </Link>
        )}

        {/* Finance shortcut — only for treasurer & main admin */}
        {(user?.role === "treasurer" || user?.role === "main_admin") && (
          <Link
            to="/admin/finance"
            data-testid="dashboard-finance-shortcut"
            className="flex items-center gap-3 bg-white border border-stone-200 rounded-2xl p-4 hover:border-stone-300 transition"
          >
            <div
              className="w-11 h-11 rounded-xl grid place-items-center shrink-0 text-white"
              style={{ backgroundColor: "var(--tenant-primary)" }}
            >
              <Landmark className="w-5 h-5" strokeWidth={1.7} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="label-eyebrow tenant-text">
                {user.role === "treasurer" ? t("roleTreasurer") : t("roleMainAdmin")}
              </div>
              <div className="font-serif-display font-semibold text-sm">{t("financeDashboard")}</div>
              <div className="text-[11px] text-stone-500 line-clamp-1">{t("financeDashboardSub")}</div>
            </div>
            <ChevronRight className="w-4 h-4 text-stone-400 shrink-0" />
          </Link>
        )}

        {/* Main-admin shortcut into full admin panel */}
        {user?.role === "main_admin" && (
          <Link
            to="/admin"
            data-testid="dashboard-admin-shortcut"
            className="flex items-center gap-3 rounded-2xl p-4 text-white hover:opacity-95 transition"
            style={{ backgroundColor: "var(--tenant-primary)" }}
          >
            <div className="w-11 h-11 rounded-xl bg-white/15 grid place-items-center shrink-0">
              <Crown className="w-5 h-5" strokeWidth={1.7} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="label-eyebrow text-white/80">{t("roleMainAdmin")}</div>
              <div className="font-serif-display font-semibold text-sm">{t("openAdminPanel")}</div>
              <div className="text-[11px] opacity-80 line-clamp-1">{t("openAdminPanelSub")}</div>
            </div>
            <ChevronRight className="w-4 h-4 opacity-80 shrink-0" />
          </Link>
        )}

        {/* Ministry tile grid — matches Landing's 3-column layout */}
        <section className="pt-1" data-testid="dashboard-quick-actions">
          <div className="px-1 mb-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="label-eyebrow">{t("exploreEyebrow")}</div>
              <h2 className="font-serif-display text-lg mt-0.5">{t("quickActions")}</h2>
            </div>
            {(selfBirthday || otherBirthdays.length > 0) && (() => {
              const names = [
                ...(selfBirthday ? [user?.first_name || user?.name?.split(" ")[0] || "you"] : []),
                ...otherBirthdays.map((b) => b.first_name || b.name?.split(" ")[0] || "someone"),
              ].filter(Boolean);
              const label =
                names.length === 1
                  ? `Today is ${names[0]}'s birthday — wish them 🎂`
                  : `${names.length} birthdays today — send wishes 🎂`;
              return (
                <Link
                  to="/birthdays"
                  data-testid="birthday-banner"
                  className="shrink-0 max-w-[62%] flex items-center gap-2 rounded-full px-3 py-2 border border-amber-200 bg-gradient-to-r from-amber-50 to-rose-50 hover:opacity-95 transition text-left"
                  aria-label={label}
                >
                  <Cake className="w-4 h-4 text-amber-600 shrink-0" strokeWidth={1.8} />
                  <span className="text-[11px] font-medium text-stone-700 leading-tight line-clamp-2">
                    {label}
                  </span>
                </Link>
              );
            })()}
          </div>

          {isEnabled(church?.feature_flags, "gallery", "member", church?._platform_disabled)
            && church?.church_code
            && !church?.gallery_in_hero && (
            <div className="mb-3">
              <GalleryStrip
                churchCode={church.church_code}
                target="/gallery"
                label={church?.gallery_title || t("gallery")}
                testId="dashboard-gallery-strip"
              />
            </div>
          )}

          <div className="grid grid-cols-3 gap-2" data-testid="feature-grid">
            {applyTileOrder(
              filterEnabled(FEATURES, church?.feature_flags, "member", church?._platform_disabled),
              church?.tile_orders?.member,
            ).map((f) => {
              const Icon = f.icon;
              const showLiveBadge = f.key === "live" && liveStatus.is_live;
              return (
                <Link
                  key={f.key}
                  to={f.to}
                  data-testid={`feature-${f.key}`}
                  className="relative aspect-square bg-white border border-stone-200 rounded-2xl p-3 hover:border-stone-300 transition flex flex-col items-center justify-center text-center gap-2 active:scale-[0.98]"
                >
                  {showLiveBadge && <LiveBadge data-testid="dashboard-live-badge" />}
                  <div
                    className="w-11 h-11 rounded-xl grid place-items-center shrink-0"
                    style={{ backgroundColor: "color-mix(in srgb, var(--tenant-primary) 12%, transparent)" }}
                  >
                    <Icon className="w-5 h-5 tenant-text" strokeWidth={1.7} />
                  </div>
                  <div className="font-serif-display font-semibold text-[13px] leading-tight">
                    {f.key === "gallery" ? (church?.gallery_title || t("gallery")) : t(f.key)}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      </div>
    </Layout>
  );
}
